import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FileText, Loader2 } from 'lucide-react';
import { HTTPError } from 'ky';
import { type TurnstileInstance } from '@marsidev/react-turnstile';
import { BottomSheet } from '@evi/ui';
import { submitStoreLead } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { TurnstileGate, TURNSTILE_SITE_KEY } from '@/components/ui/TurnstileGate';
import type { FamilyManual } from './storeCatalog';
import styles from './Store.module.css';

// ── Manual download capture sheet ──────────────────────────────────
// One email field (prefilled with the signed-in driver's email,
// editable), the shared TurnstileGate, then the lead post and the PDF
// download. Policy per the order: a Turnstile rejection blocks the
// download (bot posture); ANY other lead failure still downloads —
// the manual is the user's, the lead is ours to lose.
//
// Extracted from ProductDetailPage so both the detail page and the
// product options sheet mount the SAME component (no fork). It is fully
// self-contained: it needs only the `manual` object (path + leadSource)
// plus open/close state, and pulls the signed-in email itself.
export function ManualCaptureSheet({
  isOpen, onClose, manual,
}: {
  isOpen: boolean;
  onClose: () => void;
  manual: FamilyManual;
}) {
  const { t } = useTranslation('storeCheckout');
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const [email, setEmail] = useState(userEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Reset on OPEN, not close: the sheet can be closed while the lead
  // POST is still in flight (BottomSheet keeps swipe/backdrop live),
  // and any setState landing after a close-time reset would survive
  // into the next open as a stale thank-you or captcha error. An
  // open-time reset runs after every late write, so each open starts
  // with a fresh prefilled capture; the widget remounts fresh anyway
  // (BottomSheet renders null while closed), so the token starts null.
  useEffect(() => {
    if (isOpen) {
      setTurnstileToken(null);
      setDone(false);
      setError(null);
    }
  }, [isOpen]);

  const startDownload = (): void => {
    const a = document.createElement('a');
    a.href = manual.path;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || busy) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) return;
    setBusy(true);
    setError(null);
    try {
      await submitStoreLead(trimmed, manual.leadSource, turnstileToken ?? undefined);
    } catch (err) {
      if (err instanceof HTTPError) {
        try {
          const body = await err.response.clone().json() as { error?: { code?: string } };
          if (body?.error?.code === 'TURNSTILE_FAILED' || body?.error?.code === 'TURNSTILE_REQUIRED') {
            // Spent token: reset for a fresh challenge and hold the
            // download until the captcha clears.
            turnstileRef.current?.reset();
            setTurnstileToken(null);
            setError(t('manual.captchaError'));
            setBusy(false);
            return;
          }
        } catch {
          // Body wasn't JSON — treat as a non-Turnstile failure below.
        }
      }
      // Non-Turnstile failure: the lead is lost, the download is not.
    }
    startDownload();
    setDone(true);
    setBusy(false);
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('manual.sheetTitle')} fullBleed glass>
      {done ? (
        <div className={styles.leadForm}>
          <p className={styles.leadThanks}>
            <Check size={15} /> {t('manual.downloadStarted')}
          </p>
          <button type="button" className={styles.sheetAdd} onClick={onClose}>
            {t('manual.done')}
          </button>
        </div>
      ) : (
        <form className={styles.leadForm} onSubmit={handleSubmit} noValidate>
          <p className={styles.spBody}>
            {t('manual.formPrompt')}
          </p>
          <input
            type="email"
            className={`${styles.leadInput} clarity-mask`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('manual.emailPlaceholder')}
            required
            autoComplete="email"
            disabled={busy}
          />
          <TurnstileGate
            ref={turnstileRef}
            posture="open"
            className={styles.turnstileWrap}
            onToken={setTurnstileToken}
          />
          <button
            type="submit"
            className={styles.sheetAdd}
            disabled={busy || !email.trim() || Boolean(TURNSTILE_SITE_KEY && !turnstileToken)}
          >
            {busy
              ? <Loader2 size={16} className={styles.checkoutSpin} />
              : <><FileText size={15} /> {t('manual.getManual')}</>}
          </button>
          {error && <p className={styles.leadError}>{error}</p>}
        </form>
      )}
    </BottomSheet>
  );
}
